const normalizeWaypointLifecycleState = (state) => (
  typeof state === 'string' ? state.toLowerCase() : null
);

export const ETA_ARRIVING_THRESHOLD_MINUTES = 1;

export const isFallbackEstimate = (stop = {}) => (
  // Cumulative totals and duration fallback cannot prove this segment's distance.
  stop?.segment_distance_source === 'haversine_fallback'
);

export const getEffectiveWaypointState = (stop = {}, waypointState = null) => {
  const explicitState = normalizeWaypointLifecycleState(waypointState?.state);

  if (explicitState === 'pending') {
    return 'pending';
  }

  // This is the ETA list's lifecycle authority. Manual correction may reset a
  // stale row to pending, while persisted departure/passed evidence wins over
  // older transient states such as approaching, dwelling, or departing.
  if (stop?.waypoint_departure_time || stop?.is_passed === true) {
    return 'passed';
  }
  if (explicitState) {
    return explicitState;
  }
  if (stop?.waypoint_arrival_time) {
    return 'dwelling';
  }
  return 'pending';
};

/**
 * Keep carrier eligibility identical to the row renderer. In particular, the
 * fixed origin/final rows remain visible even if stale skip flags are present,
 * while an ordinary skipped row cannot silently absorb a disclosure that the
 * renderer will suppress.
 */
export const isEtaStopDisplaySkipped = ({
  stop = {},
  index = 0,
  totalStops = 1
} = {}) => {
  const isDepartureLocation = stop.is_departure === true || stop.point_type === 'start';
  return (stop.is_skipped === true || stop.is_manual_skip === true)
    && !isDepartureLocation
    && index !== totalStops - 1;
};

export const getEtaStopDisplayState = ({
  stop = {},
  waypointState = null,
  previousVisibleStop = null,
  previousWaypointState = null,
  displayData = null,
  tripStatus = null,
  index = 0,
  totalStops = 1
} = {}) => {
  const effectiveWaypointState = getEffectiveWaypointState(stop, waypointState);
  const isPassed = effectiveWaypointState === 'passed';
  const isDepartureLocation = stop.is_departure === true || stop.point_type === 'start';
  const isStartRow = stop.is_start_row === true || isDepartureLocation;
  const isSkipped = isEtaStopDisplaySkipped({ stop, index, totalStops });
  const displayEtaMinutes = stop.eta_minutes_from_now;
  const isWebsocketRealtime = displayData?.type === 'websocket_realtime';
  const isLiveEtaView = Boolean(displayData?.isLive || isWebsocketRealtime);
  const previousVisibleState = previousVisibleStop
    ? getEffectiveWaypointState(previousVisibleStop, previousWaypointState)
    : null;
  const previousVisibleDistance = Number(previousVisibleStop?.distance_meters_from_current);
  const previousVisibleEta = Number(previousVisibleStop?.eta_minutes_from_now);
  const previousVisibleHasPositiveLiveRoot = (
    Number.isFinite(previousVisibleDistance) && previousVisibleDistance > 0
  ) || (
    Number.isFinite(previousVisibleEta) && previousVisibleEta > 0
  );
  const isDeparting = stop.point_type === 'stop' && effectiveWaypointState === 'departing';
  const isDwelling = stop.point_type === 'stop' && effectiveWaypointState === 'dwelling';

  // Use the same suppression rule as the detailed ETA list so the summary does
  // not show ordinary remaining travel while a stop lifecycle is in progress.
  const followsDepartingStop = isLiveEtaView
    && previousVisibleState === 'departing'
    && previousVisibleHasPositiveLiveRoot
    && !isSkipped
    && !isPassed;
  const suppressLiveProgressDisplay = isLiveEtaView
    && (isDwelling || isDeparting || followsDepartingStop);
  const hasLiveEtaTimestamp = Boolean(stop.estimated_arrival_time);
  // A frozen (last-known) ETA number cannot prove live arrival proximity: the
  // location feed stopped, so the countdown is a snapshot, not observation.
  // Suppress only the ETA-derived `arriving` state and fall back to a plain
  // `eta` row. Real lifecycle evidence (arrival/departure/passage) is untouched.
  const isStalePresentation = displayData?.isStalePresentation === true;
  const hasFrozenEtaNumber = isStalePresentation
    && displayEtaMinutes !== null
    && displayEtaMinutes !== undefined
    && Number.isFinite(Number(displayEtaMinutes));
  const isPreTrip = tripStatus === 'scheduled' || tripStatus === 'dispatched';
  const usesEstimatedFallbackTime = tripStatus === 'in_progress'
    && displayData?.type === 'estimated'
    && !isLiveEtaView
    && !isDepartureLocation;

  let status = 'pending';
  if (isSkipped) {
    status = 'skipped';
  } else if (isPassed) {
    status = index === totalStops - 1 ? 'arrived' : 'departed';
  } else if (isDeparting) {
    status = 'departing';
  } else if (isDwelling) {
    status = 'arrived';
  } else if (displayData?.type === 'active_unavailable') {
    status = 'unavailable';
  } else if (
    !suppressLiveProgressDisplay
    && !isStalePresentation
    && tripStatus === 'in_progress'
    && isLiveEtaView
    && hasLiveEtaTimestamp
    && displayEtaMinutes !== null
    && displayEtaMinutes <= ETA_ARRIVING_THRESHOLD_MINUTES
  ) {
    status = 'arriving';
  } else if (
    !suppressLiveProgressDisplay
    && tripStatus === 'in_progress'
    && isLiveEtaView
    && hasLiveEtaTimestamp
    && (displayEtaMinutes > 0 || hasFrozenEtaNumber)
  ) {
    status = 'eta';
  } else if ((isPreTrip || usesEstimatedFallbackTime) && hasLiveEtaTimestamp && stop.has_eta_data !== false) {
    status = 'planned_time';
  } else if (isPreTrip && stop.has_eta_data === false) {
    status = 'no_eta';
  } else if (isDepartureLocation) {
    status = 'departure';
  }

  return {
    effectiveWaypointState,
    isPassed,
    isSkipped,
    isDepartureLocation,
    isStartRow,
    isWebsocketRealtime,
    isLiveEtaView,
    isDeparting,
    isDwelling,
    followsDepartingStop,
    suppressLiveProgressDisplay,
    hasLiveEtaTimestamp,
    isStalePresentation,
    displayEtaMinutes,
    status
  };
};
